import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRY_OPTIONS, getCountryName } from '@quds/shared';
import { cn } from '@/lib/cn';
import { Flag } from './Flag';

export interface CountrySelectProps {
  id?: string;
  value: string;
  onChange: (countryCode: string) => void;
  placeholder?: string;
  /** When set, allows clearing to empty (e.g. filter “All”). */
  allowEmpty?: boolean;
  emptyLabel?: string;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-describedby'?: string;
}

/** Searchable country select with flag + name options. Keyboard-friendly listbox. */
export function CountrySelect({
  id,
  value,
  onChange,
  placeholder = 'Select country',
  allowEmpty = false,
  emptyLabel = 'All countries',
  invalid,
  disabled,
  className,
  'aria-describedby': describedBy,
}: CountrySelectProps) {
  const autoId = useId();
  const controlId = id ?? autoId;
  const listId = `${controlId}-listbox`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [...COUNTRY_OPTIONS];
    return COUNTRY_OPTIONS.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [query]);

  const options = useMemo(() => {
    if (!allowEmpty) return filtered.map((o) => ({ value: o.value, label: o.label, empty: false as const }));
    const empty = { value: '', label: emptyLabel, empty: true as const };
    if (!query.trim()) return [empty, ...filtered.map((o) => ({ value: o.value, label: o.label, empty: false as const }))];
    return filtered.map((o) => ({ value: o.value, label: o.label, empty: false as const }));
  }, [allowEmpty, emptyLabel, filtered, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const select = (code: string) => {
    onChange(code);
    close();
  };

  const onTriggerKeyDown = (e: ReactKeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(0);
    }
  };

  const onListKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(Math.max(0, options.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[activeIndex];
      if (opt) select(opt.value);
    }
  };

  const display = value ? getCountryName(value) : allowEmpty ? emptyLabel : placeholder;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        id={controlId}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          'control flex h-control w-full items-center gap-2 pr-9 text-left',
          invalid && 'border-danger',
          disabled && 'cursor-not-allowed opacity-60',
          !value && !allowEmpty && 'text-ink-muted',
        )}
      >
        {value ? <Flag code={value} size={14} /> : null}
        <span className="min-w-0 flex-1 truncate">{display}</span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted"
        />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-dropdown mt-1 overflow-hidden rounded-md border border-line bg-surface shadow-overlay animate-scale-in"
          onKeyDown={onListKeyDown}
        >
          <div className="relative border-b border-line px-2 py-2">
            <Search size={14} aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              placeholder="Search countries…"
              className="control h-control-sm w-full pl-8 text-[13px]"
              aria-label="Search countries"
              autoComplete="off"
            />
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-labelledby={controlId}
            className="max-h-60 overflow-y-auto py-1"
          >
            {options.length === 0 ? (
              <li className="px-3 py-2 text-[13px] text-ink-muted">No matches</li>
            ) : (
              options.map((opt, index) => (
                <li key={opt.empty ? '__empty' : opt.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={opt.value === value}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      index === activeIndex ? 'bg-primary-soft text-primary' : 'hover:bg-surface-sunken',
                      opt.value === value && 'font-medium',
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => select(opt.value)}
                  >
                    {!opt.empty ? <Flag code={opt.value} size={14} /> : <span className="inline-block w-[21px]" />}
                    <span className="truncate">{opt.label}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
